import { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import { VerticalFlex } from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Text from '@eversports/react-components/primitives/Text'

import { RegistrationRoutes } from '../../routes'
import { Localized } from '../../localization/react'
import {
  CreateSelfOnboardingTokenDocument,
  CreateSelfOnboardingTokenMutation,
  CreateSelfOnboardingTokenMutationVariables,
  GetSignupSecretStatusDocument,
  GetSignupSecretStatusQuery,
  GetSignupSecretStatusQueryVariables,
} from '../../graphql'
import { PreRegistrationData } from './RegistrationStart.types'
import { AccessDeniedMessage } from './AccessDeniedMessage'
import { RegistrationForm } from './RegistrationForm'

function RegistrationStart() {
  const history = useHistory()
  const location = useLocation()
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)

  // Extract secret from URL parameters
  const urlParams = new URLSearchParams(location.search)
  const secret = urlParams.get('secret')

  // Check if secret is provided and if it's already used
  useEffect(() => {
    if (!secret) {
      setAccessDenied(true)
      return
    }

    // Check if the secret is already used
    const checkSecretStatus = async () => {
      try {
        const result = await client.query<GetSignupSecretStatusQuery, GetSignupSecretStatusQueryVariables>({
          query: GetSignupSecretStatusDocument,
          variables: { secret },
          fetchPolicy: 'no-cache', // Always fetch fresh data
        })

        if (result.data?.signupSecretStatus?.isUsed && result.data?.signupSecretStatus?.onboardingToken) {
          // Redirect to onboarding with the existing token
          history.push(`${RegistrationRoutes.VENUE}?token=${result.data.signupSecretStatus.onboardingToken}`)
        }
      } catch (error) {
        // If the secret is invalid, we'll show access denied
        setAccessDenied(true)
      }
    }

    checkSecretStatus().catch(() => {
      // Handle errors in secret status check
      setAccessDenied(true)
    })
  }, [secret, client, history])

  const [formData, setFormData] = useState<PreRegistrationData>({
    country: '',
    businessType: '',
    mainVertical: '',
    sports: [],
    previousSoftware: 'USCScheduling',
    businessModel: 'SaaS',
  })

  const handleSubmit = async (values: PreRegistrationData) => {
    setLoading(true)
    setFormData(values)

    try {
      const variables: CreateSelfOnboardingTokenMutationVariables = {
        input: {
          country: values.country,
          facilityType: values.businessType,
          mainVertical: values.mainVertical,
          sports: values.sports,
          previousSoftware: values.previousSoftware === 'None' ? null : values.previousSoftware,
          businessModel: values.businessModel,
          subscriptionPlan: values.previousSoftware === 'USCScheduling' ? 'AggregatorLight' : 'Studio',
          secret: secret!,
        },
      }

      const result = await client.mutate<CreateSelfOnboardingTokenMutation, CreateSelfOnboardingTokenMutationVariables>(
        {
          mutation: CreateSelfOnboardingTokenDocument,
          variables,
        },
      )

      if (!result.data || !result.data.createSelfOnboardingToken) {
        throw new Error('No data returned from mutation')
      }

      if (result.data.createSelfOnboardingToken.__typename === 'ExpectedErrors') {
        throw new Error('Validation errors: ' + JSON.stringify(result.data.createSelfOnboardingToken.errors))
      }

      if (result.data.createSelfOnboardingToken.__typename === 'CreateSelfOnboardingTokenResult') {
        // Redirect to the normal onboarding flow with the generated token
        history.push(`${RegistrationRoutes.VENUE}?token=${result.data.createSelfOnboardingToken.token}`)
      }
    } catch (error) {
      // Check if the error is related to invalid signup secret
      if (
        error instanceof Error &&
        (error.message.includes('Invalid signup secret') ||
          error.message.includes('Invalid or already used signup secret'))
      ) {
        setAccessDenied(true)
      } else {
        // Handle other errors appropriately - could set form errors here
        console.error('Registration error:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  // Show access denied message if no secret is provided
  if (accessDenied) {
    return <AccessDeniedMessage />
  }

  return (
    <VerticalFlex verticalSpacing="default" style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
      <Heading is="h1" variant="large">
        <Localized id="registration-start-heading" />
      </Heading>
      <Text color="muted">
        <Localized id="registration-start-subheading" />
      </Text>
      <RegistrationForm initialValues={formData} loading={loading} onSubmit={handleSubmit} />
    </VerticalFlex>
  )
}

export { RegistrationStart }
